import { useState } from "react";
import { PageTemplate } from "@/views/PageTemplate";
import { TokenOption } from "@/components/ui/types";
import { CustomTokenInput } from "@/components/ui/CustomTokenInput";
import usdcTokenImage from "@/assets/tokens/usdc.svg?url";
import piqueTokenImage from "@/assets/tokens/icon_i-onlight.svg?url";
import redeemIcon from "@/assets/icons/redeem.svg?url";
import claimIcon from "@/assets/icons/claim.svg?url";

export function RedeemPage() {
  // Define tokens
  const usdcToken: TokenOption = {
    id: "usdc",
    name: "USDC",
    icon: <img src={usdcTokenImage} alt='USDC' className='w-6 h-6 rounded-full' />,
  };

  const piqueToken: TokenOption = {
    id: "pique",
    name: "PIQUE",
    icon: <img src={piqueTokenImage} alt='PIQUE' className='w-6 h-6 rounded-full' />,
  };

  // Available tokens for selection
  const availableTokens = [usdcToken, piqueToken];

  // State declarations
  const [inputAmount, setInputAmount] = useState("1,000");
  const [claimAmount, setClaimAmount] = useState("1,000");
  const [outputAmount, setOutputAmount] = useState("642.25");
  const [selectedInputToken, setSelectedInputToken] = useState<TokenOption>(piqueToken);
  const [selectedOutputToken, setSelectedOutputToken] = useState<TokenOption>(usdcToken);
  const [activeTab, setActiveTab] = useState<"request" | "claim">("request");
  const [redeemMethod, setRedeemMethod] = useState<"arm" | "vault">("arm");

  const handleRedeem = () => {
    console.log("Redeem requested:", { inputAmount, outputAmount, method: redeemMethod });
  };

  const handleClaim = () => {
    console.log("Claim requested:", { claimAmount });
  };

  return (
    <PageTemplate title='REDEEM' subtitle='Convert back to your underlying tokens.'>
      <div className='max-w-[792px] mx-auto'>
        {/* Tabs - now with max-width and centered */}
        <div className='mb-6 flex justify-center'>
          <div className='bg-white rounded-lg flex max-w-[400px] w-full'>
            <button
              className={`flex-1 py-3 px-4 rounded-lg font-medium text-center ${
                activeTab === "request" ? "bg-gray-50 text-black" : "text-gray-500 hover:text-black"
              }`}
              onClick={() => setActiveTab("request")}
            >
              Request
            </button>
            <button
              className={`flex-1 py-3 px-4 rounded-lg font-medium text-center ${
                activeTab === "claim" ? "bg-gray-50 text-black" : "text-gray-500 hover:text-black"
              }`}
              onClick={() => setActiveTab("claim")}
            >
              Claim
            </button>
          </div>
        </div>

        <div className='rounded-xl border border-gray-200 overflow-hidden'>
          {activeTab === "request" ? (
            // REQUEST VIEW
            <>
              <div className='bg-white p-6 md:p-8'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {/* Input Token - Left aligned - now using CustomTokenInput */}
                  <CustomTokenInput
                    label='Amount to redeem...'
                    value={inputAmount}
                    onChange={(e) => setInputAmount(e.target.value)}
                    token={selectedInputToken}
                    onTokenSelect={setSelectedInputToken}
                    availableTokens={availableTokens}
                    balanceText='Available to Redeem: 687.05 PIQUE'
                    orientation='left'
                  />

                  {/* Output Token - Right aligned - now using CustomTokenInput */}
                  <CustomTokenInput
                    label='You will recieve...'
                    value={outputAmount}
                    onChange={(e) => setOutputAmount(e.target.value)}
                    token={selectedOutputToken}
                    onTokenSelect={setSelectedOutputToken}
                    availableTokens={availableTokens}
                    balanceText='Current Balance: 0.00 USDC'
                    orientation='right'
                    readOnly={true}
                  />
                </div>

                {/* Select Duration Section */}
                <div className='mt-6'>
                  <h3 className='font-medium text-black mb-4 text-left'>Select Duration</h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* Redeem via ARM Option */}
                    <div
                      className={`border rounded-lg p-4 cursor-pointer bg-gray-50 ${redeemMethod === "arm" ? "border-black" : "border-gray-200"}`}
                      onClick={() => setRedeemMethod("arm")}
                    >
                      <div className='flex items-center justify-between mb-3'>
                        <span className='font-medium text-black text-xs'>Redeem via the ARM</span>
                        <div
                          className={`w-5 h-5 rounded-full border ${
                            redeemMethod === "arm" ? "border-black bg-black" : "border-gray-300"
                          } flex items-center justify-center`}
                        >
                          {redeemMethod === "arm" && <div className='w-2 h-2 bg-white rounded-full'></div>}
                        </div>
                      </div>
                      <div className='space-y-1 text-sm'>
                        <div className='flex items-center text-black text-xs'>
                          <svg className='w-4 h-4 mr-2' viewBox='0 0 24 24' fill='none'>
                            <path d='M12 8v4l3 3' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                            <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='2' />
                          </svg>
                          <span>Wait time: ~1min</span>
                        </div>
                        <div className='flex items-center text-black text-xs'>
                          <svg className='w-4 h-4 mr-2' viewBox='0 0 24 24' fill='none'>
                            <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                          </svg>
                          <span>Rate: 1:1</span>
                        </div>
                      </div>
                    </div>

                    {/* Redeem via PIQUE Vault Option */}
                    <div
                      className={`border rounded-lg p-4 cursor-pointer bg-gray-50 ${redeemMethod === "vault" ? "border-black" : "border-gray-200"}`}
                      onClick={() => setRedeemMethod("vault")}
                    >
                      <div className='flex items-center justify-between mb-3'>
                        <span className='font-medium text-black text-xs'>Redeem via PIQUE Vault</span>
                        <div
                          className={`w-5 h-5 rounded-full border ${
                            redeemMethod === "vault" ? "border-black bg-black" : "border-gray-300"
                          } flex items-center justify-center`}
                        >
                          {redeemMethod === "vault" && <div className='w-2 h-2 bg-white rounded-full'></div>}
                        </div>
                      </div>
                      <div className='space-y-1 text-sm'>
                        <div className='flex items-center text-black text-xs'>
                          <svg className='w-4 h-4 mr-2' viewBox='0 0 24 24' fill='none'>
                            <path d='M12 8v4l3 3' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                            <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='2' />
                          </svg>
                          <span>Wait time: a few days</span>
                        </div>
                        <div className='flex items-center text-black text-xs'>
                          <svg className='w-4 h-4 mr-2' viewBox='0 0 24 24' fill='none'>
                            <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                          </svg>
                          <span>Rate: 1:1</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Review - Request */}
              <div className='bg-gray-50 p-6 md:p-8'>
                <h3 className='font-medium text-black mb-4 text-left text-xs'>Transaction Review</h3>
                <div className='space-y-1 text-sm'>
                  <div className='flex items-start'>
                    <svg className='w-5 h-5 mr-3 text-black flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-black text-left'>You are requesting to redeem 1,000 PIQUE tokens for 642.25 USDC tokens.</span>
                  </div>
                  <div className='flex items-start'>
                    <svg className='w-5 h-5 mr-3 text-black flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-black text-left'>Currently, 1 PIQUE is worth 0.64225 USDC.</span>
                  </div>
                  <div className='flex items-start'>
                    <svg className='w-5 h-5 mr-3 text-black flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-black text-left'>
                      You&apos;ve selected to redeem via the ARM, which has a wait time of ~1 minute and has a rate of 1:1.
                    </span>
                  </div>
                  <div className='flex items-start'>
                    <svg className='w-5 h-5 mr-3 text-black flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-black text-left'>
                      Based on the current{" "}
                      <a href='#' className='underline text-black'>
                        slippage settings
                      </a>{" "}
                      of 0.1%, the minimum you will receive is 641.60 USDC tokens.
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // CLAIM VIEW
            <>
              <div className='bg-white p-6 md:p-8'>
                <div className='mb-6'>
                  <p className='text-black mb-2 text-left'>Available to claim...</p>
                  <div className='border border-gray-200 rounded-lg overflow-hidden'>
                    <div className='flex items-center'>
                      <input
                        type='text'
                        value={claimAmount}
                        onChange={(e) => setClaimAmount(e.target.value)}
                        placeholder='0'
                        className='flex-1 w-full p-4 text-black text-xl font-regular focus:outline-none'
                      />
                      <div className='pr-4 flex items-center gap-2'>
                        <img src={usdcTokenImage} alt='USDC' className='w-6 h-6 rounded-full' />
                        <span className='text-black'>USDC</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Boxes */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  {/* Your Requests */}
                  <div className='border border-gray-200 rounded-lg p-4 text-left'>
                    <p className='font-medium text-black mb-3'>Your Requests</p>
                    <div className='space-y-2'>
                      <div className='flex items-center text-black'>
                        <svg className='w-4 h-4 mr-2' viewBox='0 0 24 24' fill='none'>
                          <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                        <span>0</span>
                      </div>
                      <div className='flex items-center text-black'>
                        <svg className='w-4 h-4 mr-2' viewBox='0 0 24 24' fill='none'>
                          <path d='M12 8v4l3 3' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                          <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='2' />
                        </svg>
                        <span>0</span>
                      </div>
                    </div>
                  </div>

                  {/* Your Pending Amount */}
                  <div className='border border-gray-200 rounded-lg p-4 text-left'>
                    <p className='font-medium text-black mb-3'>Your Pending Amount</p>
                    <div className='flex items-center text-black mt-4'>
                      <img src={usdcTokenImage} alt='USDC' className='w-5 h-5 mr-2' />
                      <span>1,234.54 USD</span>
                    </div>
                  </div>

                  {/* Your Claims */}
                  <div className='border border-gray-200 rounded-lg p-4 text-left'>
                    <p className='font-medium text-black mb-3'>Your Claims</p>
                    <p className='text-black text-sm mt-4'>You currently have no withdrawal requests.</p>
                  </div>
                </div>
              </div>

              {/* Transaction Review - Claim */}
              <div className='bg-gray-50 p-6 md:p-8'>
                <h3 className='font-medium text-black mb-4 text-left'>Transaction Review</h3>
                <div className='space-y-1 text-sm'>
                  <div className='flex items-start'>
                    <svg className='w-5 h-5 mr-3 text-black flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-black text-left'>You are claiming 1,000 USDC tokens.</span>
                  </div>
                  <div className='flex items-start'>
                    <svg className='w-5 h-5 mr-3 text-black flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-black text-left'>Currently, 1 USDC is worth $1.00 USD.</span>
                  </div>
                  <div className='flex items-start'>
                    <svg className='w-5 h-5 mr-3 text-black flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                    <span className='text-black text-left'>
                      Based on the current{" "}
                      <a href='#' className='underline text-black'>
                        slippage settings
                      </a>{" "}
                      of 0.1%, the minimum you will receive is 999.00 USDC tokens.
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Centered Button - changes based on active tab */}
        <div className='flex justify-center mt-6'>
          {activeTab === "request" ? (
            <button
              onClick={handleRedeem}
              className='flex items-center justify-center gap-2 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors'
            >
              Request to Redeem
              <img src={redeemIcon} alt='Redeem' className='w-4 h-4' />
            </button>
          ) : (
            <button
              onClick={handleClaim}
              className='flex items-center justify-center gap-2 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors'
            >
              Claim Now
              <img src={claimIcon} alt='Claim' className='w-4 h-4' />
            </button>
          )}
        </div>
      </div>
    </PageTemplate>
  );
}
